/** Returning from Lead Details to where the user came from (2026-11-05).
 *
 * A page that opens a lead passes its own address (path + query string,
 * which is where list pages such as Leads keep their page, filters and
 * search) as router state; Lead Details' back button returns there.
 * Opened any other way (a pasted URL, a refresh after the state is gone),
 * it falls back to the Leads list. */

export interface LeadDetailState {
  /** The address to return to, e.g. "/leads?page=2&leadStatusId=3". */
  from?: string;
}

type Where = { pathname: string; search: string };

/** Router state for opening a lead from `location`, plus any extra state. */
export const leadDetailState = <T extends object>(
  location: Where,
  extra?: T,
): LeadDetailState & T =>
  ({ ...(extra ?? {}), from: `${location.pathname}${location.search}` }) as LeadDetailState & T;

const BACK_LABELS: Array<[RegExp, string]> = [
  [/^\/leads(\?|$)/, 'Back to leads'],
  [/^\/lead-approvals/, 'Back to Lead Approvals'],
  [/^\/personal-appointments/, 'Back to Personal Appointments'],
  [/^\/completed-consultations/, 'Back to Completed Consultation'],
  [/^\/inquiries/, 'Back to Inquiries'],
  [/^\/application-reviews/, 'Back to Application Reviews'],
  [/^\/(dashboard)?(\?|$)/, 'Back to dashboard'],
];

/** Where Lead Details' back button goes, and what it says. Only an in-app
 * path is accepted (never an external URL). */
export const leadBackTarget = (state: unknown): { to: string; label: string } => {
  const from = (state as LeadDetailState | null)?.from;
  if (
    typeof from === 'string' &&
    from.startsWith('/') &&
    !from.startsWith('//') &&
    !/^\/leads\/\d/.test(from)
  ) {
    const label = BACK_LABELS.find(([re]) => re.test(from))?.[1] ?? 'Back';
    return { to: from, label };
  }
  return { to: '/leads', label: 'Back to leads' };
};
